import { z } from "zod";
import { paginationQuerySchema } from "./api";
import { normalizeOrigin } from "./origins";

/** FR-ACC-05: roles, most powerful first. */
export const roleSchema = z.enum(["OWNER", "ADMIN", "DEVELOPER", "TESTER"]);

export const userSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  image: z.string().nullable(),
});

export const membershipSummarySchema = z.object({
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  workspaceSlug: z.string(),
  role: roleSchema,
});

/** GET /api/v1/me */
export const meResponseSchema = z.object({
  user: userSummarySchema,
  memberships: z.array(membershipSummarySchema),
});

export type Role = z.infer<typeof roleSchema>;
export type UserSummary = z.infer<typeof userSummarySchema>;
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;
export type MeResponse = z.infer<typeof meResponseSchema>;

const selectorSchema = z.string().trim().min(1).max(200);
const fieldKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[\w.:[\]-]+$/, "Use letters, digits and _ - . : [ ] only");

const privacyFields = {
  maskInputs: z.boolean(),
  captureBodies: z.boolean(),
  maskSelectors: z.array(selectorSchema).max(50),
  blockSelectors: z.array(selectorSchema).max(50),
  redactKeys: z.array(fieldKeySchema).max(100),
  allowKeys: z.array(fieldKeySchema).max(100),
};

/** FR-PRIV-02..06: what capture records. Lives on the workspace; projects can turn bodies off. */
export const privacySettingsSchema = z.object({
  maskInputs: privacyFields.maskInputs.default(true),
  captureBodies: privacyFields.captureBodies.default(true),
  maskSelectors: privacyFields.maskSelectors.default([]),
  blockSelectors: privacyFields.blockSelectors.default([]),
  redactKeys: privacyFields.redactKeys.default([]),
  allowKeys: privacyFields.allowKeys.default([]),
});

/** A privacy change: only the fields sent are changed; the rest keep their stored values. */
export const privacySettingsUpdateSchema = z.object(privacyFields).partial();

/** FR-RET-01 */
export const retentionDaysSchema = z.union([
  z.literal(30),
  z.literal(90),
  z.literal(180),
  z.literal(365),
]);

const logsUrlTemplateSchema = z
  .string()
  .trim()
  .url()
  .max(500)
  .refine((value) => value.includes("{requestId}"), "Include {requestId} where the id goes")
  .refine((value) => /^https?:\/\//.test(value), "Start with https://");

export const workspaceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  role: roleSchema,
  retentionDays: retentionDaysSchema,
  allowPublicLinks: z.boolean(),
  logsUrlTemplate: z.string().nullable(),
  privacy: privacySettingsSchema,
  createdAt: z.string(),
});

const nameSchema = z.string().trim().min(1, "Enter a name").max(80);

const originListSchema = z
  .array(z.string())
  .max(50)
  .transform((values, context) => {
    const origins: string[] = [];
    for (const value of values) {
      const origin = normalizeOrigin(value);
      if (!origin) {
        context.addIssue({ code: "custom", message: `"${value}" is not a site address` });
        return z.NEVER;
      }
      if (!origins.includes(origin)) origins.push(origin);
    }
    return origins;
  });

export const keyPrefixSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9]{1,9}$/, "2 to 10 letters or digits, starting with a letter");

export const projectSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  name: z.string(),
  keyPrefix: z.string(),
  allowedOrigins: z.array(z.string()),
  captureBodies: z.boolean(),
  createdAt: z.string(),
});

export const projectCreateSchema = z.object({
  name: nameSchema,
  keyPrefix: keyPrefixSchema.optional(),
  allowedOrigins: originListSchema.default([]),
  captureBodies: z.boolean().default(true),
});

export const projectUpdateSchema = z
  .object({
    name: nameSchema,
    keyPrefix: keyPrefixSchema,
    allowedOrigins: originListSchema,
    captureBodies: z.boolean(),
  })
  .partial();

/** POST /workspaces: the onboarding step also creates the first project. */
export const workspaceCreateSchema = z.object({
  name: nameSchema,
  project: projectCreateSchema.optional(),
});

export const workspaceUpdateSchema = z
  .object({
    name: nameSchema,
    retentionDays: retentionDaysSchema,
    allowPublicLinks: z.boolean(),
    logsUrlTemplate: logsUrlTemplateSchema.nullable(),
    privacy: privacySettingsUpdateSchema,
  })
  .partial();

export type PrivacySettings = z.infer<typeof privacySettingsSchema>;
export type Workspace = z.infer<typeof workspaceSchema>;
export type WorkspaceCreate = z.infer<typeof workspaceCreateSchema>;
export type WorkspaceUpdate = z.infer<typeof workspaceUpdateSchema>;
export type Project = z.infer<typeof projectSchema>;
export type ProjectCreate = z.infer<typeof projectCreateSchema>;
export type ProjectUpdate = z.infer<typeof projectUpdateSchema>;

/** FR-ACC-04, FR-ACC-05: a workspace member as the Members screen shows them. */
export const memberSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  image: z.string().nullable(),
  role: roleSchema,
  joinedAt: z.string(),
  /** Last time any of their sessions was used; null when never signed in since joining. */
  lastActiveAt: z.string().nullable(),
});

export const memberUpdateSchema = z.object({ role: roleSchema });

export const inviteCreateSchema = z.object({
  emails: z
    .array(z.string().trim().toLowerCase().email("Enter valid email addresses"))
    .min(1, "Enter at least one email address")
    .max(50),
  role: roleSchema,
});

export const inviteSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  role: roleSchema,
  expiresAt: z.string(),
  createdAt: z.string(),
});

/** Returned once on create: the link holds the token, which is never stored or shown again. */
export const createdInviteSchema = inviteSchema.extend({
  url: z.string().url(),
  /** False when SMTP is not configured: the admin copies the link instead. */
  emailed: z.boolean(),
});

/** GET /invites/:token, shown on the accept page before and after sign-in. */
export const invitePreviewSchema = z.object({
  workspaceName: z.string(),
  role: roleSchema,
  email: z.string(),
  status: z.enum(["pending", "expired", "accepted", "revoked"]),
});

export const resetLinkSchema = z.object({ url: z.string().url() });

export type Member = z.infer<typeof memberSchema>;
export type MemberUpdate = z.infer<typeof memberUpdateSchema>;
export type InviteCreate = z.infer<typeof inviteCreateSchema>;
export type Invite = z.infer<typeof inviteSchema>;
export type CreatedInvite = z.infer<typeof createdInviteSchema>;
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

/** FR-AUD-01: one audit log entry as Admins see it. */
export const auditEntrySchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  actor: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
  targetType: z.string(),
  targetId: z.string(),
  data: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  createdAt: z.string(),
});

export type AuditEntry = z.infer<typeof auditEntrySchema>;

/** GET /workspaces/:ws/audit query. */
export const auditQuerySchema = paginationQuerySchema.extend({
  action: z.string().max(64).optional(),
});

export type AuditQuery = z.infer<typeof auditQuerySchema>;

/** GET /client-config: what a recorder in the page needs (FR-PRIV-01..06, FR-NET-05). */
export const clientConfigSchema = z.object({
  projectId: z.string().uuid(),
  allowedOrigins: z.array(z.string()),
  capture: z.object({
    maskInputs: z.boolean(),
    /** Workspace and project settings combined. */
    captureBodies: z.boolean(),
    maskSelectors: z.array(z.string()),
    blockSelectors: z.array(z.string()),
    redactBodyKeys: z.array(z.string()),
    allowBodyKeys: z.array(z.string()),
  }),
  /** URL prefixes never recorded: Repro's own API. */
  ignoreUrls: z.array(z.string()),
});

export const clientConfigQuerySchema = z.object({ projectId: z.string().uuid().optional() });

export type ClientConfig = z.infer<typeof clientConfigSchema>;

/** GET /auth-options: which sign-in methods this server offers (FR-ACC-01, FR-ACC-06). */
export const authOptionsSchema = z.object({
  google: z.boolean(),
  /** Email is set up: password reset by email and verification are available. */
  email: z.boolean(),
  /** FR-EXT-03: only these extension ids get a token from /extension/connect. */
  extension: z.object({ ids: z.array(z.string()), installUrl: z.string().nullable() }),
  /** FR-ACC-08: only these domains may sign up; empty means any. */
  emailDomains: z.array(z.string()).default([]),
});

export type AuthOptions = z.infer<typeof authOptionsSchema>;
